import { useState, useEffect, useCallback } from "react";
import { troncRecordApi } from "../services/api";
import { useLocationContext } from "../contexts/LocationContext";
import { useTenantContext } from "../contexts/TenantContext";

/**
 * Helper to extract data from API response
 * Handles both { success, data } and { Success, Data } formats
 */
const extractData = (response) => {
  if (!response) return { success: false, data: null };

  const success = response.success ?? response.Success ?? false;
  const data = response.data ?? response.Data ?? null;
  const message = response.message ?? response.Message ?? null;

  return { success, data, message };
};

/**
 * Hook for fetching and managing TRONC records
 * Automatically filters by current location if one is selected
 * For Global Admins, also filters by selected tenant
 */
export function useTroncRecords(options = {}) {
  const {
    fromDate,
    toDate,
    locationId: explicitLocationId,
    unsyncedOnly = false,
    autoFetch = true,
    useCurrentLocation = true,
  } = options;
  const { currentLocationId } = useLocationContext();
  const { currentTenantId } = useTenantContext();

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Use explicit locationId if provided, otherwise use current location context
  const effectiveLocationId =
    explicitLocationId ?? (useCurrentLocation ? currentLocationId : null);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {};
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
      if (effectiveLocationId) params.locationId = effectiveLocationId;
      if (unsyncedOnly) params.unsyncedOnly = true;
      // For Global Admins, filter by selected tenant
      if (currentTenantId) params.tenantId = currentTenantId;

      const response = await troncRecordApi.getAll(params);
      const { success, data, message } = extractData(response);

      if (success) {
        setRecords(data || []);
      } else {
        setError(message || "Failed to fetch TRONC records");
      }
    } catch (err) {
      setError(err.message || "Failed to fetch TRONC records");
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, effectiveLocationId, unsyncedOnly, currentTenantId]);

  useEffect(() => {
    if (autoFetch) {
      fetchRecords();
    }
  }, [fetchRecords, autoFetch]);

  return {
    records,
    loading,
    error,
    refetch: fetchRecords,
    locationId: effectiveLocationId,
    tenantId: currentTenantId,
  };
}

/**
 * Hook for TRONC summaries
 */
export function useTroncSummary() {
  const { currentTenantId } = useTenantContext();
  const [summaryByLocation, setSummaryByLocation] = useState([]);
  const [summaryByMonth, setSummaryByMonth] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchSummaryByLocation = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (currentTenantId) params.tenantId = currentTenantId;
      const response = await troncRecordApi.getSummaryByLocation(params);
      const { success, data } = extractData(response);
      if (success) {
        setSummaryByLocation(data || []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentTenantId]);

  const fetchSummaryByMonth = useCallback(
    async (year = null) => {
      setLoading(true);
      try {
        const params = {};
        if (year) params.year = year;
        if (currentTenantId) params.tenantId = currentTenantId;
        const response = await troncRecordApi.getSummaryByMonth(params);
        const { success, data } = extractData(response);
        if (success) {
          setSummaryByMonth(data || []);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    },
    [currentTenantId],
  );

  const fetchLocations = useCallback(async () => {
    try {
      const params = {};
      if (currentTenantId) params.tenantId = currentTenantId;
      const response = await troncRecordApi.getLocations(params);
      const { success, data } = extractData(response);
      if (success) {
        setLocations(data || []);
      }
    } catch (err) {
      setError(err.message);
    }
  }, [currentTenantId]);

  const fetchTotal = useCallback(
    async (fromDate, toDate, locationId = null) => {
      try {
        const params = { fromDate, toDate };
        if (locationId) params.locationId = locationId;
        if (currentTenantId) params.tenantId = currentTenantId;
        const response = await troncRecordApi.getTotal(params);
        const { success, data } = extractData(response);
        if (success) {
          return data;
        }
      } catch (err) {
        setError(err.message);
      }
      return 0;
    },
    [currentTenantId],
  );

  useEffect(() => {
    fetchSummaryByLocation();
    fetchSummaryByMonth();
    fetchLocations();
  }, [fetchSummaryByLocation, fetchSummaryByMonth, fetchLocations]);

  return {
    summaryByLocation,
    summaryByMonth,
    locations,
    loading,
    error,
    fetchSummaryByLocation,
    fetchSummaryByMonth,
    fetchTotal,
    tenantId: currentTenantId,
  };
}

/**
 * Hook for TRONC record mutations
 */
export function useTroncMutations() {
  const { currentLocationId } = useLocationContext();
  const { currentTenantId } = useTenantContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const createRecord = async (recordData) => {
    setLoading(true);
    setError(null);

    try {
      // Ensure locationId is set if not provided
      const data = {
        ...recordData,
        locationId: recordData.locationId ?? currentLocationId,
      };
      const response = await troncRecordApi.create(data);
      return extractData(response);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateRecord = async (id, recordData) => {
    setLoading(true);
    setError(null);

    try {
      const response = await troncRecordApi.update(id, recordData);
      return extractData(response);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status, notes = null) => {
    setLoading(true);
    setError(null);

    try {
      const response = await troncRecordApi.updateStatus(id, { status, notes });
      return extractData(response);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteRecord = async (id, reason) => {
    setLoading(true);
    setError(null);

    try {
      const response = await troncRecordApi.delete(id, reason);
      return extractData(response);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const syncToSona = async (id) => {
    setLoading(true);
    setError(null);

    try {
      const response = await troncRecordApi.syncToSona(id);
      return extractData(response);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    createRecord,
    updateRecord,
    updateStatus,
    deleteRecord,
    syncToSona,
    currentLocationId,
    currentTenantId,
  };
}

export default useTroncRecords;
